import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, RefreshCw, ExternalLink, Award, Save } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const STATUSES = ["submitted", "approved", "finalist", "winner", "denied", "all"];

/**
 * TalentContestAdminTab — admin console for the Next Top Creator contest.
 *
 * Two tabs:
 *   1. Entries — filter by status, review, approve / deny / promote to finalist / mark winner
 *   2. Settings — edit rules text + version, prize, open/close dates, gallery toggle
 */
export default function TalentContestAdminTab() {
  const [tab, setTab] = useState("entries");
  return (
    <div data-testid="admin-contest-tab" className="space-y-4">
      <div className="flex items-center gap-2">
        <Trophy className="w-5 h-5 text-tv-accent" />
        <h2 className="font-display font-black text-2xl text-white">Talent Search · $50k Contest</h2>
      </div>
      <div className="flex gap-2 border-b border-white/10">
        <button onClick={() => setTab("entries")} data-testid="admin-contest-tab-entries" className={`px-3 py-2 text-sm font-bold ${tab === "entries" ? "text-tv-accent border-b-2 border-tv-accent" : "text-white/60"}`}>Entries</button>
        <button onClick={() => setTab("settings")} data-testid="admin-contest-tab-settings" className={`px-3 py-2 text-sm font-bold ${tab === "settings" ? "text-tv-accent border-b-2 border-tv-accent" : "text-white/60"}`}>Contest Settings</button>
      </div>
      {tab === "entries" ? <EntriesList /> : <ContestSettings />}
    </div>
  );
}

function EntriesList() {
  const [status, setStatus] = useState("submitted");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState({});
  const [score, setScore] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get(`/contest/admin/entries?status=${status}`),
        api.get(`/contest/admin/stats`),
      ]);
      setRows(r.data || []); setStats(s.data || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const decide = async (e, newStatus) => {
    try {
      await api.post(`/contest/admin/entries/${e.id}/decision`, {
        status: newStatus, note: note[e.id] || "", admin_score: score[e.id] ? parseInt(score[e.id]) : null,
      });
      toast.success(`Marked ${newStatus}`); load();
    } catch (er) { toast.error(formatApiError(er)); }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center">
        {["total", "submitted", "approved", "finalists", "winner", "denied"].map((k) => (
          <div key={k} className="bg-white/5 border border-white/10 rounded p-2">
            <div className="text-[9px] uppercase text-white/50 font-black">{k}</div>
            <div className="font-display font-black text-xl">{stats[k] ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {STATUSES.map((s) => (
            <button key={s} data-testid={`admin-contest-filter-${s}`} onClick={() => setStatus(s)} className={`text-xs font-bold px-3 py-1.5 rounded ${status === s ? "bg-tv-accent text-black" : "bg-white/5 text-white/70"}`}>{s}</button>
          ))}
        </div>
        <button onClick={load} className="ml-auto text-white/60 hover:text-white text-sm inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      {loading ? <div className="text-white/60">Loading…</div> : (
        <div className="space-y-2" data-testid="admin-contest-entries-list">
          {rows.length === 0 && <div className="text-white/40 py-6 text-center">No {status} entries.</div>}
          {rows.map((e) => (
            <div key={e.id} data-testid={`admin-entry-${e.id}`} className="bg-white/5 rounded-lg p-3">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-lg">{e.stage_name}</div>
                    <span className={`text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      e.status === "submitted" ? "bg-amber-500/25 text-amber-200" :
                      e.status === "approved" ? "bg-sky-500/25 text-sky-200" :
                      e.status === "finalist" ? "bg-fuchsia-500/25 text-fuchsia-200" :
                      e.status === "winner" ? "bg-tv-accent text-black" :
                      "bg-red-500/25 text-red-200"
                    }`}>{e.status === "winner" && <Award className="w-3 h-3 inline mr-0.5" />}{e.status}</span>
                    {e.admin_score != null && <span className="text-white/60 text-xs">Score: {e.admin_score}</span>}
                  </div>
                  <div className="text-white/70 text-sm mt-0.5">{e.user_email} · {e.city} · {e.age_range}</div>
                  {e.genres?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {e.genres.map((g) => <span key={g} className="text-[10px] bg-white/10 text-white/70 px-1.5 py-0.5 rounded">{g}</span>)}
                    </div>
                  )}
                  {e.bio && <p className="text-white/60 text-sm mt-2 whitespace-pre-wrap line-clamp-3">{e.bio}</p>}
                  <div className="mt-2">
                    <div className="text-[10px] uppercase tracking-wider font-black text-tv-accent">Why they're the next top creator</div>
                    <p className="text-white text-sm whitespace-pre-wrap">{e.why_me}</p>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-[11px] flex-wrap">
                    {e.video_url && <a href={e.video_url} target="_blank" rel="noreferrer" className="text-tv-accent hover:underline inline-flex items-center gap-1">Video ({e.video_source_type}) <ExternalLink className="w-3 h-3" /></a>}
                    {e.social_instagram && <a className="text-tv-accent hover:underline" href={`https://instagram.com/${e.social_instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer">IG: {e.social_instagram}</a>}
                    {e.social_tiktok && <a className="text-tv-accent hover:underline" href={`https://tiktok.com/${e.social_tiktok.startsWith("@") ? e.social_tiktok : "@" + e.social_tiktok}`} target="_blank" rel="noreferrer">TT: {e.social_tiktok}</a>}
                    {e.social_youtube && <a className="text-tv-accent hover:underline" href={e.social_youtube} target="_blank" rel="noreferrer">YT ↗</a>}
                  </div>
                  <div className="mt-2 text-[10px] text-white/40">
                    Signed: <strong className="text-white/70">"{e.signed_name}"</strong> · rules {e.rules_version} · IP {e.rules_agreed_ip || "—"} · {e.rules_agreed_at && new Date(e.rules_agreed_at).toLocaleString()}
                  </div>
                  <div className="mt-1 text-[10px] text-white/40">
                    Eligibility: {e.eligibility && Object.entries(e.eligibility).map(([k, v]) => <span key={k} className={`ml-1 ${v ? "text-emerald-300" : "text-red-300"}`}>{v ? "✓" : "✗"} {k}</span>)}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <input
                  data-testid={`admin-entry-score-${e.id}`}
                  type="number" min="0" max="100" placeholder="Score"
                  value={score[e.id] ?? ""}
                  onChange={(ev) => setScore({ ...score, [e.id]: ev.target.value })}
                  className="w-20 bg-black/40 border border-white/10 rounded px-2 py-1 text-xs"
                />
                <input
                  data-testid={`admin-entry-note-${e.id}`}
                  placeholder="Decision note"
                  value={note[e.id] || ""}
                  onChange={(ev) => setNote({ ...note, [e.id]: ev.target.value })}
                  className="flex-1 min-w-[180px] bg-black/40 border border-white/10 rounded px-2 py-1 text-xs"
                />
                <button data-testid={`admin-entry-approve-${e.id}`} onClick={() => decide(e, "approved")} className="bg-sky-500 hover:bg-sky-400 text-black font-black text-xs px-2.5 py-1.5 rounded">Approve</button>
                <button data-testid={`admin-entry-finalist-${e.id}`} onClick={() => decide(e, "finalist")} className="bg-fuchsia-500 hover:bg-fuchsia-400 text-black font-black text-xs px-2.5 py-1.5 rounded">Finalist</button>
                <button data-testid={`admin-entry-winner-${e.id}`} onClick={() => { if (window.confirm(`Mark ${e.stage_name} as the WINNER? This will demote any existing winner.`)) decide(e, "winner"); }} className="bg-tv-accent hover:bg-yellow-400 text-black font-black text-xs px-2.5 py-1.5 rounded inline-flex items-center gap-1"><Award className="w-3 h-3" /> Winner</button>
                <button data-testid={`admin-entry-deny-${e.id}`} onClick={() => decide(e, "denied")} className="bg-red-500/20 hover:bg-red-500/40 text-red-200 font-black text-xs px-2.5 py-1.5 rounded">Deny</button>
                <button
                  data-testid={`admin-entry-pay-${e.id}`}
                  onClick={async () => {
                    const usd = window.prompt(`Pay ${e.stage_name} — USD amount (e.g. 50000 for grand prize):`, e.status === "winner" ? "50000" : "1000");
                    if (!usd) return;
                    const cents = Math.round(Number(usd) * 100);
                    if (!cents || cents <= 0) { toast.error("Enter a valid USD amount"); return; }
                    const note = window.prompt("Optional note (e.g. 'Grand prize', 'Runner-up'):", e.status === "winner" ? "Grand prize · Next Top Creator" : "");
                    try {
                      await api.post(`/contest/admin/entries/${e.id}/pay`, { cash_cents: cents, note: (note || "").trim() });
                      toast.success(`Paid $${(cents / 100).toLocaleString()} to ${e.stage_name}`);
                      load();
                    } catch (err) { toast.error(formatApiError(err)); }
                  }}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black font-black text-xs px-2.5 py-1.5 rounded"
                >Pay $</button>
                {e.payouts?.length > 0 && (
                  <span className="text-[10px] text-emerald-300 font-bold ml-1">
                    Paid ${(e.payouts.reduce((s, p) => s + (p.cash_cents || 0), 0) / 100).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContestSettings() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", prize_usd: 0, rules_text: "", rules_version: "",
    open_at: "", close_at: "", judging_until: "",
    enabled: true, show_public_gallery: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contest/admin/config");
      setCfg(data);
      setForm({
        title: data.title, prize_usd: data.prize_usd,
        rules_text: data.rules_text, rules_version: data.rules_version,
        open_at: data.open_at || "", close_at: data.close_at || "", judging_until: data.judging_until || "",
        enabled: data.enabled, show_public_gallery: data.show_public_gallery,
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/contest/admin/config", form);
      toast.success("Contest settings saved"); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading || !cfg) return <div className="text-white/60">Loading…</div>;

  return (
    <div className="space-y-3" data-testid="admin-contest-settings">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <div className="text-[10px] uppercase font-black text-white/60 mb-1">Title</div>
          <input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase font-black text-white/60 mb-1">Prize (USD)</div>
          <input type="number" value={form.prize_usd || 0} onChange={(e) => setForm({ ...form, prize_usd: parseFloat(e.target.value) || 0 })} className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase font-black text-white/60 mb-1">Open at (ISO)</div>
          <input value={form.open_at || ""} onChange={(e) => setForm({ ...form, open_at: e.target.value })} placeholder="2026-03-01T00:00:00Z" className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase font-black text-white/60 mb-1">Close at (ISO)</div>
          <input value={form.close_at || ""} onChange={(e) => setForm({ ...form, close_at: e.target.value })} placeholder="2026-05-31T23:59:00Z" className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white" />
        </label>
      </div>
      <div className="flex items-center gap-4">
        <label className="inline-flex items-center gap-1.5 text-sm"><input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="accent-tv-accent" /> Accepting entries</label>
        <label className="inline-flex items-center gap-1.5 text-sm"><input type="checkbox" checked={!!form.show_public_gallery} onChange={(e) => setForm({ ...form, show_public_gallery: e.target.checked })} className="accent-tv-accent" /> Show public gallery</label>
        <div className="ml-auto text-white/50 text-xs">Current rules version: <strong>{cfg.rules_version}</strong></div>
      </div>
      <label className="block">
        <div className="text-[10px] uppercase font-black text-white/60 mb-1">Official Rules text (saving will bump version)</div>
        <textarea
          data-testid="admin-contest-rules-text"
          rows={14}
          value={form.rules_text || ""}
          onChange={(e) => setForm({ ...form, rules_text: e.target.value })}
          className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white font-mono text-xs"
        />
      </label>
      <button
        data-testid="admin-contest-save"
        onClick={save} disabled={saving}
        className="bg-tv-accent hover:bg-yellow-400 text-black font-black px-4 py-2 rounded inline-flex items-center gap-2"
      ><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save contest settings"}</button>
    </div>
  );
}
